import {
  AppBase,
  baseFromDoc,
  FirestoreBaseDoc,
  makeDocConverter,
} from '../base/firestore-model-base'
import type { Timestamp } from 'firebase/firestore'

/**
 * config/system ドキュメント
 *
 * - メンテナンス期間の管理
 * - 利用規約PDFのストレージURL
 *
 * - `SystemConfigDoc`: Firestore に保存される形（snake_case / Timestamp）
 * - `SystemConfig`: アプリで扱う形（camelCase / Date）
 */

export type MaintenancePeriodDoc = {
  start_date: Timestamp
  end_date: Timestamp
  is_complete: boolean
}

export type MaintenancePeriod = {
  startDate: Date
  endDate: Date
  isComplete: boolean
}

export type SystemConfigDoc = FirestoreBaseDoc & {
  version_admin: string
  version_school: string
  version_town: string
  terms_of_service: string
  maintenance: MaintenancePeriodDoc[]
}

export type SystemConfig = AppBase & {
  adminVersion: string
  schoolVersion: string
  townVersion: string
  termsOfService: string
  maintenance: MaintenancePeriod[]
}

export type AppVersionTarget = 'admin' | 'school' | 'town'

export const systemConfigDocConverter = makeDocConverter<SystemConfigDoc>()

function toMaintenancePeriod(doc: MaintenancePeriodDoc): MaintenancePeriod {
  return {
    startDate: doc.start_date?.toDate?.() ?? new Date(0),
    endDate: doc.end_date?.toDate?.() ?? new Date(0),
    isComplete: doc.is_complete,
  }
}

export function toSystemConfig(id: string, doc: SystemConfigDoc, path: string): SystemConfig {
  const base = baseFromDoc(id, doc, path)
  return {
    ...base,
    adminVersion: doc.version_admin ?? '0.0.0',
    schoolVersion: doc.version_school ?? '0.0.0',
    townVersion: doc.version_town ?? '0.0.0',
    termsOfService: doc.terms_of_service ?? '',
    maintenance: (doc.maintenance ?? []).map(toMaintenancePeriod),
  }
}

export function getSystemConfigVersion(config: SystemConfig, target: AppVersionTarget): string {
  switch (target) {
    case 'admin':
      return config.adminVersion
    case 'school':
      return config.schoolVersion
    case 'town':
      return config.townVersion
  }
}

function parseVersion(version: string): number[] {
  return version
    .split('.')
    .map((part) => {
      const matched = part.match(/\d+/)
      return matched ? Number(matched[0]) : 0
    })
}

/** semver 風の `x.y.z` を数値比較する。 */
export function compareVersionStrings(a: string, b: string): number {
  const left = parseVersion(a)
  const right = parseVersion(b)
  const maxLength = Math.max(left.length, right.length)
  for (let i = 0; i < maxLength; i += 1) {
    const leftPart = left[i] ?? 0
    const rightPart = right[i] ?? 0
    if (leftPart !== rightPart) {
      return leftPart < rightPart ? -1 : 1
    }
  }
  return 0
}

export function isRemoteVersionNewer(currentVersion: string, remoteVersion: string): boolean {
  return compareVersionStrings(currentVersion, remoteVersion) < 0
}

/**
 * 現在メンテナンス中かどうか。
 * - 開始時刻を過ぎており、かつ未完了（is_complete=false）ならメンテナンス中とみなす
 * - 終了判定は endDate ではなく is_complete を優先する
 */
export function isUnderMaintenance(config: SystemConfig, now: Date = new Date()): boolean {
  return config.maintenance.some((p) => !p.isComplete && p.startDate <= now)
}

/**
 * 現在有効なメンテナンス期間（1件のみ返す。複数ある場合は最初の1件）。
 * - 開始済みかつ未完了の期間を返す（終了は is_complete で管理）
 */
export function getActiveMaintenancePeriod(
  config: SystemConfig,
  now: Date = new Date()
): MaintenancePeriod | null {
  const active = config.maintenance.find((p) => !p.isComplete && p.startDate <= now)
  return active ?? null
}
